import { Button, Progress, Space, Table, Tooltip, Typography } from "antd";
import { EmiStatus, ILoan } from "store/types";

import { ColumnsType } from "antd/es/table";
import ErrorRenderer from "components/util-components/ErrorRenderer";
import HiddenComponent from "components/util-components/HiddenComponent";
import { IndianRuppeeView } from "components/products/product.util.components";
import { LoansListActions } from "store/slices/loansListSlice";
import { RootState } from "store/store";
import { ShowProductsInTable } from "./loans.util-components";
import { momentDateTimeFormat } from "components/util-components/dateTimeFormattor";
import { useAppDispatch } from "store/hooks";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const LoansListAssignDispatcherPending = () => {
  const { loansList } = useSelector((state: RootState) => state);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { details, loading, error, errorStatus } =
    loansList.assignDispatcherPending;

  const columns: ColumnsType<ILoan> = [
    {
      title: t("Order Id"),
      key: "loanId",
      render: (_, record) => {
        return <Typography.Text copyable>{record?.id}</Typography.Text>;
      },
    },
    {
      title: t("labels.customers.name"),
      key: "customerName",
      render: (_, record) => {
        return (
          <Typography.Link
            onClick={() => {
              navigate(`/customers/${record?.customerId}/dashboard`);
            }}
          >
            {record?.customerDetails?.firstName +
              " " +
              record?.customerDetails?.lastName}
          </Typography.Link>
        );
      },
    },
    {
      title: t("Order Amount"),
      key: "loanAmount",
      render: (_, record) => {
        return (
          <>
            <IndianRuppeeView value={record?.loanAmount?.toString()} />
          </>
        );
      },
    },
    {
      title: t("Outstanding balance"),
      key: "outstandingBalance",
      render: (_, record) => {
        return (
          <>
            <IndianRuppeeView value={record?.outstandingAmount?.toString()} />
          </>
        );
      },
    },
    {
      title: t("Start Date"),
      key: "startDate",
      render: (_, record) => {
        return <> {momentDateTimeFormat(record?.applyTime, "date")}</>;
      },
    },
    {
      title: t("Tenure"),
      key: "endDate",
      render: (_, record) => {
        return (
          <>
            <Tooltip
              title={
                <>
                  {t("Ends on ") +
                    momentDateTimeFormat(record?.endDate, "date")}
                </>
              }
            >
              {record?.tenure + " months"}
            </Tooltip>
          </>
        );
      },
    },
    {
      title: t("Products"),
      key: "products",
      render: (_, record) => {
        return <ShowProductsInTable productList={record?.purchasedItems} />;
      },
    },
    {
      title: t("EMIs paid"),
      key: "emis",
      render: (_, record) => {
        const paidEmi =
          record?.emis?.filter((emi) => {
            return emi?.status === EmiStatus.PAID;
          }).length || 0;
        const totalEmi = record?.emis?.length || 0;
        const paidEmiPercentage = (paidEmi / totalEmi) * 100;
        return (
          <>
            <Progress
              percent={paidEmiPercentage}
              format={() => (
                <Typography.Text
                  strong
                >{`${paidEmi} / ${totalEmi}`}</Typography.Text>
              )}
            />
          </>
        );
      },
    },

    {
      title: t("Actions"),
      key: "actions",
      render: (_, record) => {
        return (
          <>
            <Button
              onClick={() => {
                navigate(`/orders/details/${record?.id}`);
              }}
              type="link"
            >
              {t("View")}
            </Button>
          </>
        );
      },
    },
  ];
  return (
    <HiddenComponent
      hidden={error}
      fallBack={<ErrorRenderer status={errorStatus} />}
    >
      <Table
        bordered
        sticky={true}
        rowKey={(data) => data?.id}
        loading={loading}
        size={"small"}
        dataSource={details?.items}
        columns={columns}
        scroll={{
          x: "calc(100vw - 150px)",
          scrollToFirstRowOnChange: true,
        }}
        pagination={{
          showSizeChanger: true,
          pageSize: loansList?.loansPagination?.pageSize,
          current: loansList?.loansPagination?.pageToken,
          total: details?.total,
          onChange: (pageNumber, pageSize) => {
            if (loansList?.loansPagination?.pageSize !== pageSize) {
              dispatch(
                LoansListActions.setLoansPagination({
                  pageSize: pageSize,
                  pageToken: 1,
                })
              );
            } else {
              dispatch(LoansListActions.setLoansPageToken(pageNumber));
            }
          },
          showTotal: () => {
            return (
              <Space>
                <Typography.Text>{`${t(
                  "labels.loans.totalCount"
                )}:`}</Typography.Text>
                <Typography.Text>{details?.pageSize || 0}</Typography.Text>
              </Space>
            );
          },
        }}
      />
    </HiddenComponent>
  );
};

export default LoansListAssignDispatcherPending;
